/**
 * WelcomePanel — shown over the empty graph canvas on first load.
 * Fades out once the user starts a scan (elements.length > 0 or scanning).
 */

import { useGraphStore } from '@/store/graphStore';

const MONO = { fontFamily: '"JetBrains Mono", monospace' } as const;

const FEATURES = [
  { icon: '◈', color: '#58a6ff', label: 'Infrastructure Graph',   desc: 'Interactive DNS node map — A, NS, MX, CNAME, TXT visually linked' },
  { icon: '◉', color: '#3fb950', label: 'Geo Intelligence',        desc: 'Deck.gl globe — plot every IP to its physical datacenter or city' },
  { icon: '⬡', color: '#f85149', label: 'Threat Overlays',         desc: 'Live threat feed correlation — malicious IPs lit up in real time' },
  { icon: '◫', color: '#e3b341', label: 'SSL / Cert Pivot',        desc: 'SAN-based pivoting — find every domain sharing a certificate' },
  { icon: '◷', color: '#bc8cff', label: 'Traffic Intel',           desc: 'Carrier-grade analytics — NXDOMAIN ratios, spikes, latency p95' },
  { icon: '◈', color: '#79c0ff', label: 'DNSSEC Validation',       desc: 'Chain-of-trust analysis from root to leaf for any domain' },
  { icon: '◉', color: '#3fb950', label: 'Passive DNS History',     desc: 'Historical resolution snapshots — track infrastructure changes over time' },
  { icon: '◫', color: '#58a6ff', label: 'Charts Gallery',          desc: '14 live charts — query types, response codes, ASN market share, CAs' },
];

export function WelcomePanel() {
  const elements = useGraphStore((s) => s.elements);
  const scanning = useGraphStore((s) => s.scanning);

  // Hide once a scan starts or nodes appear
  if (scanning || elements.length > 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0e14',
        padding: '32px 24px',
        overflowY: 'auto',
      }}
    >
      <div style={{ width: '100%', maxWidth: 860 }}>

        {/* ── Hero ── */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            display: 'inline-block',
            fontSize: 11, fontWeight: 700, letterSpacing: 3,
            color: '#58a6ff', textTransform: 'uppercase', marginBottom: 16,
            padding: '4px 14px', border: '1px solid #58a6ff44',
            borderRadius: 20, ...MONO,
          }}>
            DNS Intelligence Platform
          </div>

          <h1 style={{
            fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800,
            color: '#e6edf3', margin: '0 0 16px', lineHeight: 1.15,
            ...MONO,
          }}>
            Welcome to{' '}
            <span style={{ color: '#58a6ff' }}>DNSScience.io</span>
          </h1>

          <p style={{
            fontSize: 14, color: '#8b949e', maxWidth: 560,
            margin: '0 auto 32px', lineHeight: 1.7, ...MONO,
          }}>
            Carrier-grade DNS intelligence built by engineers who ran DNS on tier-1 ISP networks and international financial services firms.
            Scan any domain to map its full infrastructure, threat exposure, certificate chain, and traffic patterns.
          </p>

          {/* CTA arrow */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, color: '#58a6ff', fontSize: 12, ...MONO,
          }}>
            <span style={{ color: '#8b949e' }}>Enter a domain in the scan box</span>
            <span style={{ fontSize: 18, lineHeight: 1 }}>↑</span>
            <span style={{ color: '#8b949e' }}>to get started</span>
          </div>
        </div>

        {/* ── Feature grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 12,
        }}>
          {FEATURES.map((f) => (
            <div
              key={f.label}
              style={{
                background: '#161b22',
                border: '1px solid #21262d',
                borderRadius: 10,
                padding: '14px 16px',
                display: 'flex',
                gap: 14,
                alignItems: 'flex-start',
              }}
            >
              <span style={{ fontSize: 20, color: f.color, lineHeight: 1, marginTop: 1 }}>{f.icon}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#e6edf3', marginBottom: 4, ...MONO }}>
                  {f.label}
                </div>
                <div style={{ fontSize: 11, color: '#8b949e', lineHeight: 1.5, ...MONO }}>
                  {f.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Footer stats ── */}
        <div style={{
          marginTop: 36, display: 'flex', justifyContent: 'center',
          gap: 32, flexWrap: 'wrap',
        }}>
          {[
            { value: '25+', label: 'Intel plugins' },
            { value: '41',  label: 'DB tables' },
            { value: '14',  label: 'Live charts' },
            { value: 'gRPC', label: 'Custom DNS server' },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#58a6ff', ...MONO }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2, ...MONO }}>{s.label}</div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
